import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo4Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 4 BWV 775
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV775/bach-invention-04/bach-invention-04-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 4 BWV 775.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-5016">
      <Navbar data-id="element-5017" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-5018">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-5019">
          <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=300&fit=crop" alt="Invention No. 4" className="w-full h-64 md:h-80 object-cover" data-id="element-5020" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-5021" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-5022">
            <div className="max-w-7xl mx-auto" data-id="element-5023">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-5024">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-5025" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-5026">
                Invention No. 4 in D minor
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-5027">BWV 775</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-5028">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-5029">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-5030">
          <div className="flex items-center space-x-2" data-id="element-5031">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-5032">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-5033" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5034">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-5035">
                  <ZoomInIcon className="h-5 w-5" data-id="element-5036" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-5037">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-5038">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-5039" />
              <span className="hidden sm:inline" data-id="element-5040">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-5041">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-5042" />
              <span className="hidden sm:inline" data-id="element-5043">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-5044">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5045">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-5046">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 4 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-5047" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-5048">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5049">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-5050">
            <div data-id="element-5051">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5052">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-5053">
                <div data-id="element-5054">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5055">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5056">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-5057">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5058">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5059">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-5060">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5061">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5062">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-5063">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5064">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5065">
                    D minor
                  </dd>
                </div>
                <div data-id="element-5066">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5067">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5068">
                    BWV 775
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-5069">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5070">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5071">
                The fourth of Bach's fifteen Two-Part Inventions, this piece in
                D minor showcases a more dramatic and expressive character
                compared to its predecessors. Written as pedagogical works for
                keyboard students, the Inventions teach the art of two-voice
                counterpoint while developing musical ideas through systematic
                elaboration. This invention features a distinctive descending
                subject that creates a sense of gravity and seriousness,
                characteristic of the minor mode. The piece demonstrates Bach's
                mastery of invertible counterpoint, where the two voices
                frequently exchange roles, with the subject appearing in both
                upper and lower registers. The continuous sixteenth-note motion
                requires technical precision and evenness, while the minor key
                lends the work an introspective, contemplative quality. This
                invention serves as both an essential study in baroque keyboard
                technique and a compelling musical statement that reveals Bach's
                genius for creating profound expression through rigorous
                contrapuntal discipline.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-5072" />
    </div>;
};